import React, { useState, useEffect } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceArea, Legend,
} from "recharts";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  TrendUp, TrendDown, Sparkle, Globe, Briefcase,
  Warning, ChartLineUp, Lightning, ArrowsClockwise, X,
} from "@phosphor-icons/react";

const BIAS_TINT = {
  "strong bull": { bg: "#064e3b", fg: "#6ee7b7", chip: "bg-emerald-500 text-emerald-950" },
  "bull bias":   { bg: "#065f46", fg: "#a7f3d0", chip: "bg-emerald-400 text-emerald-950" },
  "neutral":     { bg: "#374151", fg: "#cbd5e1", chip: "bg-slate-400 text-slate-950" },
  "bear bias":   { bg: "#7f1d1d", fg: "#fecaca", chip: "bg-rose-400 text-rose-950" },
  "strong bear": { bg: "#450a0a", fg: "#fca5a5", chip: "bg-rose-600 text-white" },
};

const KIND_TINT = {
  crash:         { ring: "ring-rose-400/40", bg: "bg-rose-50",     fg: "text-rose-900",     label: "CRASH" },
  bull:          { ring: "ring-emerald-400/40", bg: "bg-emerald-50",  fg: "text-emerald-900",  label: "BULL" },
  recovery:      { ring: "ring-amber-400/40",  bg: "bg-amber-50",     fg: "text-amber-900",    label: "RECOVERY" },
  regime_shift:  { ring: "ring-violet-400/40", bg: "bg-violet-50",    fg: "text-violet-900",   label: "REGIME SHIFT" },
};

const REGION_TINT = {
  US: { stroke: "#60a5fa", glow: "#3b82f6", label: "S&P 500" },
  IN: { stroke: "#fb923c", glow: "#f97316", label: "Nifty 50" },
};

export default function AdminGlobalMarketsPage() {
  const today = new Date();
  const thisYear = today.getUTCFullYear();
  const thisMonth = today.getUTCMonth() + 1;
  // Default range: from 5yrs back to 5yrs forward (10-yr window centred on now)
  const [startYM, setStartYM] = useState(`${thisYear - 5}-01`);
  const [endYM, setEndYM]     = useState(`${thisYear + 5}-12`);
  const [region, setRegion]   = useState("US");
  const [rangeData, setRangeData] = useState(null);
  const [report, setReport] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [busy, setBusy] = useState(false);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [dotDetail, setDotDetail] = useState(null);

  const fetchRange = async () => {
    const sm = startYM.split("-").map(Number);
    const em = endYM.split("-").map(Number);
    if (sm.length !== 2 || em.length !== 2) return toast.error("Enter both dates as YYYY-MM");
    const [sy, smo] = sm; const [ey, emo] = em;
    if (sy < 1930 || ey > 2080 || ey < sy || (ey === sy && emo < smo)) {
      return toast.error("1930-01 ≤ start ≤ end ≤ 2080-12");
    }
    setBusy(true); setRangeData(null); setReport(null); setNarrative(null);
    try {
      const { data } = await api.post("/admin/tools/global-markets/range", {
        start_year: sy, start_month: smo, end_year: ey, end_month: emo,
      });
      setRangeData(data);
      setReport(data.anchor_report);
      // Claude narrative for the anchor year
      setNarrativeBusy(true);
      api.post("/admin/tools/global-markets/narrative", { report: data.anchor_report })
        .then(({ data: nd }) => setNarrative(nd?.narrative || null))
        .catch(() => toast.error("Narrative engine timed out — see deterministic data below."))
        .finally(() => setNarrativeBusy(false));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to fetch range.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { fetchRange(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => { e.preventDefault(); fetchRange(); };

  return (
    <div className="min-h-[100dvh] bg-[#0b1020] text-slate-100 flex flex-col" data-testid="admin-global-markets-page">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex items-center gap-2 text-amber-400 text-[11px] uppercase tracking-[0.25em]">
          <Globe size={14} weight="duotone" /> Super-Admin · Global Markets Forecaster
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl mt-3 font-light leading-tight">
          Vedic Macro · <span className="italic text-amber-400">Jupiter · Saturn · Rahu</span>
        </h1>
        <p className="text-slate-400 text-sm mt-3 max-w-3xl font-light">
          Outer-planet sidereal transits drive macro regime. Pattern-matched against ~45 curated US + India events from 1929 to 2026.
          Past months → real price action coloured by MoM returns. Future months → analog-based forecast: median MoM of all historical
          months sharing ≥2 of the 3 outer-planet signs (Saturn weighted ×2) — grounded in real market behaviour, not compound projection.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex items-end gap-3 flex-wrap" data-testid="range-form">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">From</span>
            <input
              type="month" value={startYM} min="1930-01" max="2080-12"
              onChange={(e) => setStartYM(e.target.value)}
              className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm font-mono focus:border-amber-400 outline-none"
              data-testid="range-start"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-slate-400">To</span>
            <input
              type="month" value={endYM} min="1930-01" max="2080-12"
              onChange={(e) => setEndYM(e.target.value)}
              className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm font-mono focus:border-amber-400 outline-none"
              data-testid="range-end"
            />
          </label>
          <button
            type="submit" disabled={busy}
            className="px-5 py-2 rounded-md bg-amber-500 text-slate-950 text-sm font-medium hover:bg-amber-400 disabled:opacity-50"
            data-testid="range-submit"
          >
            {busy ? "Computing…" : "Run forecast"}
          </button>
          <div className="flex gap-1.5 ml-1 flex-wrap">
            {[
              ["1928-2009", "1928-01", "2009-12"],
              ["1985-1992", "1985-01", "1992-12"],
              ["2007-2010", "2007-01", "2010-12"],
              ["2019-2022", "2019-01", "2022-12"],
              ["2024-2030", "2024-01", "2030-12"],
            ].map(([l, s, e]) => (
              <button
                key={l} type="button"
                onClick={() => { setStartYM(s); setEndYM(e); setTimeout(fetchRange, 50); }}
                className="px-2 py-1 rounded text-xs font-mono bg-slate-800 hover:bg-slate-700 text-slate-300"
                data-testid={`quick-${l}`}
              >
                {l}
              </button>
            ))}
          </div>
        </form>

        {busy && (
          <div className="mt-8 flex items-center gap-3 text-amber-300">
            <ArrowsClockwise size={18} weight="duotone" className="animate-spin" />
            Loading market curve…
          </div>
        )}

        {rangeData && (
          <MarketChart
            rangeData={rangeData}
            region={region}
            setRegion={setRegion}
            onDotClick={setDotDetail}
          />
        )}

        {report && (
          <ReportView
            report={report}
            narrative={narrative}
            narrativeBusy={narrativeBusy}
          />
        )}

        {dotDetail && (
          <DotDetailPanel detail={dotDetail} onClose={() => setDotDetail(null)} />
        )}
      </main>
      <Footer />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// INTERACTIVE CHART
// ─────────────────────────────────────────────────────────────────────────
function MarketChart({ rangeData, region, setRegion, onDotClick }) {
  const curve = rangeData.curves[region];
  const rTint = REGION_TINT[region];

  // Build chart data with explicit dot-flag based on turn_points
  const turnSet = new Set(curve.turn_points);
  const data = curve.series.map((row, idx) => ({
    ...row,
    idx,
    isTurn: turnSet.has(idx),
    biasColor: BIAS_TINT[row.bias_band]?.bg || "#374151",
  }));

  // Recharts clones the dot element and passes cx/cy/payload as props.
  const dotElement = <TurnDot region={region} onClick={onDotClick} />;

  // Bias bands for the chart background — colour-coded by band streaks
  const bands = buildBandStripes(data);

  return (
    <section className="mt-8 rounded-3xl p-5 sm:p-7 relative overflow-hidden" data-testid="market-chart"
      style={{ background: "radial-gradient(ellipse at top left, rgba(251,146,60,0.10), transparent 50%), radial-gradient(ellipse at bottom right, rgba(96,165,250,0.12), transparent 50%), #0f172a", border: "1px solid #1e293b" }}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 flex items-center gap-2">
            <ChartLineUp size={14} weight="duotone" /> Market Curve · {curve.start_iso} → {curve.end_iso}
          </div>
          <h2 className="font-serif-display text-2xl sm:text-3xl mt-2 font-light">
            <span style={{ color: rTint.glow }}>{rTint.label}</span>
            <span className="text-slate-400 text-base ml-3">
              start {fmtNum(curve.start_value)} · end {fmtNum(curve.end_value)} ·
              peak {fmtNum(curve.peak_value)} · trough {fmtNum(curve.trough_value)}
            </span>
          </h2>
        </div>
        <div className="flex items-center gap-1 rounded-lg p-1 bg-slate-900/70 border border-slate-700" data-testid="region-toggle">
          {["US", "IN"].map((r) => (
            <button
              key={r} type="button"
              onClick={() => setRegion(r)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold tracking-wider transition-all ${
                region === r ? "text-slate-950" : "text-slate-400 hover:text-slate-200"
              }`}
              style={region === r ? { background: REGION_TINT[r].glow } : {}}
              data-testid={`region-${r}`}
            >
              {REGION_TINT[r].label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 24, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`line-${region}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={rTint.stroke} stopOpacity={0.4} />
                <stop offset="50%" stopColor={rTint.stroke} stopOpacity={1} />
                <stop offset="100%" stopColor={rTint.glow} stopOpacity={0.7} />
              </linearGradient>
            </defs>
            {/* Bias-band background stripes */}
            {bands.map((b, i) => (
              <ReferenceArea
                key={i} x1={b.x1} x2={b.x2}
                fill={b.colour} fillOpacity={0.10}
                stroke="none"
              />
            ))}
            {/* Forecast region overlay */}
            {data.some((d) => d.is_forecast) && (
              <ReferenceArea
                x1={data.find((d) => d.is_forecast)?.date_iso}
                x2={data[data.length - 1]?.date_iso}
                fill="#7c3aed" fillOpacity={0.05} stroke="none"
              />
            )}
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis
              dataKey="date_iso"
              tickFormatter={(v) => {
                const dt = new Date(v);
                return dt.getUTCMonth() === 0 ? String(dt.getUTCFullYear()) : "";
              }}
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={{ stroke: "#334155" }}
              tickLine={{ stroke: "#334155" }}
            />
            <YAxis
              tick={{ fill: "#94a3b8", fontSize: 11 }}
              axisLine={{ stroke: "#334155" }}
              tickLine={{ stroke: "#334155" }}
              width={60}
              tickFormatter={fmtNum}
            />
            <Tooltip content={<ChartTooltip region={region} />} />
            <Legend
              wrapperStyle={{ paddingTop: 8, color: "#94a3b8", fontSize: 11 }}
              formatter={LegendFormatter(rTint)}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={`url(#line-${region})`}
              strokeWidth={2.5}
              dot={dotElement}
              activeDot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] text-slate-400">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: "#fbbf24", boxShadow: "0 0 8px #fbbf24" }} />
          Turn-point (bull↔bear flip or major event)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded" style={{ background: BIAS_TINT["strong bull"].bg }} />
          Strong bull
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded" style={{ background: BIAS_TINT["bear bias"].bg }} />
          Bear bias
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded" style={{ background: "rgba(124, 58, 237, 0.3)" }} />
          Forecast region
        </span>
      </div>
    </section>
  );
}


// Build a stable formatter so we don't define a component inline in Legend.
function LegendFormatter(rTint) {
  // Returns a render function suitable for Recharts' `Legend formatter` prop.
  // Defined at module scope so React doesn't churn the subtree on each render.
  return () => `${rTint.label} (click any dot for transit detail)`;
}


function buildBandStripes(data) {
  if (!data.length) return [];
  const stripes = [];
  let curBand = data[0].bias_band;
  let x1 = data[0].date_iso;
  for (let i = 1; i < data.length; i++) {
    if (data[i].bias_band !== curBand) {
      stripes.push({ x1, x2: data[i - 1].date_iso, colour: BIAS_TINT[curBand]?.bg || "#374151" });
      curBand = data[i].bias_band;
      x1 = data[i].date_iso;
    }
  }
  stripes.push({ x1, x2: data[data.length - 1].date_iso, colour: BIAS_TINT[curBand]?.bg || "#374151" });
  return stripes;
}


// Custom dot — solid gold halo on turn-points, invisible elsewhere
function TurnDot({ cx, cy, payload, region, onClick }) {
  if (!payload?.isTurn) return null;
  const rTint = REGION_TINT[region];
  return (
    <g style={{ cursor: "pointer" }} onClick={() => onClick && onClick({ ...payload, region })}>
      <circle cx={cx} cy={cy} r={9} fill={rTint.glow} opacity={0.2} />
      <circle cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#fde68a" strokeWidth={1.5} />
      {payload.event && (
        <circle cx={cx} cy={cy} r={11} fill="none" stroke="#fbbf24" strokeWidth={1.2} opacity={0.6}>
          <animate attributeName="r" from="11" to="18" dur="1.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="0.6" to="0" dur="1.8s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
}


function ChartTooltip({ active, payload, region }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  const t = d.transits || {};
  return (
    <div className="rounded-lg p-3 bg-slate-950/95 border border-amber-400/40 text-slate-100 text-xs space-y-1.5 shadow-2xl">
      <div className="font-mono font-bold text-amber-300">{d.label}</div>
      <div className="text-base font-serif-display">{fmtNum(d.value)}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${BIAS_TINT[d.bias_band]?.chip || "bg-slate-400 text-slate-950"}`}>
          {d.bias_band.toUpperCase()}
          {d.mom_return_pct != null && (
            <span className="ml-1">{d.mom_return_pct > 0 ? "+" : ""}{d.mom_return_pct}%</span>
          )}
        </span>
        {d.is_forecast && (
          <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-violet-500/40 text-violet-200">
            FORECAST
          </span>
        )}
        {d.event && (
          <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-rose-500/40 text-rose-100">
            EVENT · click for detail
          </span>
        )}
      </div>
      {d.transit_bias_band && d.transit_bias_band !== d.bias_band && (
        <div className="text-[10px] text-slate-400">
          Transit engine read: <span className="text-amber-200">{d.transit_bias_band}</span>
        </div>
      )}
      <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-800">
        J · {t.Jupiter?.rashi} &nbsp; S · {t.Saturn?.rashi} &nbsp; R · {t.Rahu?.rashi}
      </div>
    </div>
  );
}


function fmtNum(n) {
  if (n == null) return "—";
  if (Math.abs(n) >= 10000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}


// ─────────────────────────────────────────────────────────────────────────
// DOT DETAIL · slide-in side panel
// ─────────────────────────────────────────────────────────────────────────
function DotDetailPanel({ detail, onClose }) {
  const { transits, event, region, label, value, bias_band, transit_bias_band, transit_bias_reasons, is_forecast, mom_return_pct } = detail;
  const rTint = REGION_TINT[region];
  return (
    <div className="fixed inset-0 z-50" data-testid="dot-detail-panel">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-slate-950 border-l border-amber-400/30 overflow-y-auto shadow-2xl animate-slide-in"
        style={{ filter: "none", backdropFilter: "none" }}
      >
        <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800 p-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300">
              {region === "US" ? "S&P 500" : "Nifty 50"} · {label}
            </div>
            <div className="font-serif-display text-2xl mt-1 font-light" style={{ color: rTint.glow }}>
              {fmtNum(value)}
            </div>
            {mom_return_pct != null && (
              <div className={`text-xs mt-1 font-mono ${mom_return_pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                {mom_return_pct >= 0 ? "+" : ""}{mom_return_pct}% MoM
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400" data-testid="close-panel">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded ${BIAS_TINT[bias_band]?.chip || "bg-slate-400 text-slate-950"}`}>
              PRICE · {bias_band.toUpperCase()}
            </span>
            {transit_bias_band && transit_bias_band !== bias_band && (
              <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded ${BIAS_TINT[transit_bias_band]?.chip || "bg-slate-400 text-slate-950"} opacity-70`}>
                TRANSIT · {transit_bias_band.toUpperCase()}
              </span>
            )}
            {is_forecast && (
              <span className="text-[10px] font-bold tracking-wider px-2 py-1 rounded bg-violet-500/40 text-violet-200">
                FORECAST
              </span>
            )}
            {event && (
              <span className={`text-[10px] font-bold tracking-wider px-2 py-1 rounded ${KIND_TINT[event.kind]?.bg} ${KIND_TINT[event.kind]?.fg}`}>
                {KIND_TINT[event.kind]?.label}
              </span>
            )}
          </div>

          {/* Outer-planet transit */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-3 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" /> Outer-planet transit
            </div>
            <div className="space-y-2.5">
              {["Jupiter", "Saturn", "Rahu"].map((p) => (
                <div key={p} className="rounded-lg p-3 bg-slate-900/70 border border-slate-700" data-testid={`panel-transit-${p}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-amber-200">{p}</div>
                    {transits[p]?.retrograde && <span className="text-[10px] text-rose-300">retrograde</span>}
                  </div>
                  <div className="mt-1 font-serif-display text-lg">{transits[p]?.rashi}</div>
                  <div className="text-xs text-slate-400">{transits[p]?.nakshatra} · lord {transits[p]?.nakshatra_lord}</div>
                </div>
              ))}
            </div>
            {Array.isArray(transit_bias_reasons) && transit_bias_reasons.length > 0 && (
              <div className="mt-3 rounded-lg p-3 bg-amber-950/30 border border-amber-500/30 text-xs text-amber-100 space-y-1.5 font-light">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">Transit reasoning</div>
                {transit_bias_reasons.map((r, i) => <div key={i}>· {r}</div>)}
              </div>
            )}
          </div>

          {/* Event detail or forecast read */}
          {event ? (
            <div className="rounded-xl p-4 bg-gradient-to-br from-rose-950/40 to-amber-950/40 border border-amber-400/40">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5">
                <Lightning size={12} weight="fill" /> {event.label}
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-3 mb-3 font-mono">
                <span>{event.date}</span>
                <span className="font-bold text-amber-200">{event.drawdown > 0 ? "+" : ""}{event.drawdown}%</span>
                {event.recovery_months && <span>recovery {event.recovery_months}mo</span>}
              </div>
              <p className="text-sm text-slate-200 font-light leading-snug">{event.summary}</p>
            </div>
          ) : (
            <div className="rounded-xl p-4 bg-slate-900/60 border border-slate-700">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5">
                <Lightning size={12} weight="fill" /> Bias regime
              </div>
              <p className="text-sm text-slate-200 font-light leading-snug">
                {is_forecast
                  ? `Projected ${bias_band} regime (${mom_return_pct != null ? `${mom_return_pct >= 0 ? "+" : ""}${mom_return_pct}% MoM` : "neutral"}) — return derived from the median MoM of all historical months sharing ≥2 of the 3 outer-planet signs (Saturn weighted ×2). This is analog-based forecasting, not compound-step projection.`
                  : `Price action ran ${bias_band} this month (${mom_return_pct != null ? `${mom_return_pct >= 0 ? "+" : ""}${mom_return_pct}% MoM` : "neutral"}). The transit engine independently reads "${transit_bias_band}" — when these two diverge, the chart is telling you the macro story is more nuanced than transits alone suggest.`}
              </p>
            </div>
          )}

          {/* Sector picks (drawn from sector_verdict at each transit) */}
          <SectorPicks transits={transits} />
        </div>
      </div>
    </div>
  );
}


function SectorPicks({ transits }) {
  // Pull verdicts from the static SECTOR_MAP on the frontend (mirror of backend logic).
  // We don't carry sector picks per-row in the curve payload to keep it small;
  // here we use a small lookup that mirrors the most-bullish / most-avoid signals.
  const bull = new Set();
  const bear = new Set();
  ["Jupiter", "Saturn", "Rahu"].forEach((p) => {
    const m = SECTOR_LITE[`${p}:${transits[p]?.rashi}`];
    if (m) { m.bullish.forEach((x) => bull.add(x)); m.bearish.forEach((x) => bear.add(x)); }
  });
  if (!bull.size && !bear.size) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {bull.size > 0 && (
        <div className="rounded-xl p-3 bg-emerald-950/40 border border-emerald-500/30">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1"><TrendUp size={10} weight="bold" /> Trade</div>
          <div className="flex flex-wrap gap-1.5">
            {[...bull].slice(0, 6).map((s, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200">{s}</span>
            ))}
          </div>
        </div>
      )}
      {bear.size > 0 && (
        <div className="rounded-xl p-3 bg-rose-950/40 border border-rose-500/30">
          <div className="text-[10px] uppercase tracking-widest text-rose-300 mb-2 flex items-center gap-1"><TrendDown size={10} weight="bold" /> Avoid</div>
          <div className="flex flex-wrap gap-1.5">
            {[...bear].slice(0, 5).map((s, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-rose-500/15 text-rose-200">{s}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Lite sector map mirrored from backend — only the most-bullish picks per
// planet×sign to keep frontend bundle small. Used by SectorPicks.
const SECTOR_LITE = {
  "Jupiter:Aries":      { bullish: ["Defence-tech", "Sports"],                      bearish: ["Slow utilities"] },
  "Jupiter:Taurus":     { bullish: ["Banking", "FMCG", "Jewellery"],                bearish: ["Distressed-asset funds"] },
  "Jupiter:Gemini":     { bullish: ["Tech/IT", "Media", "Telecom"],                 bearish: ["Heavy industry"] },
  "Jupiter:Cancer":     { bullish: ["Real estate", "Hotels", "Dairy"],              bearish: ["Mining"] },
  "Jupiter:Leo":        { bullish: ["Gold", "Entertainment", "Mega-cap"],           bearish: ["Small-caps"] },
  "Jupiter:Virgo":      { bullish: ["Healthcare", "Pharma", "QSR"],                 bearish: ["Aviation"] },
  "Jupiter:Libra":      { bullish: ["Wealth mgmt", "Diamonds", "Hospitality"],      bearish: ["Defence", "Steel"] },
  "Jupiter:Scorpio":    { bullish: ["Insurance", "Pharma R&D", "Biotech"],          bearish: ["Consumer retail"] },
  "Jupiter:Sagittarius":{ bullish: ["Banking", "Higher-ed", "Aviation"],            bearish: ["Defence (saturated)"] },
  "Jupiter:Capricorn":  { bullish: ["Infra", "Cement", "Govt bonds"],               bearish: ["Glamour retail"] },
  "Jupiter:Aquarius":   { bullish: ["Renewables", "Fintech", "Aerospace"],          bearish: ["Traditional banking"] },
  "Jupiter:Pisces":     { bullish: ["Pharma", "Shipping", "AI"],                    bearish: ["Hard metals"] },
  "Saturn:Aries":       { bullish: ["Defence", "Heavy machinery"],                  bearish: ["Discretionary", "Aviation"] },
  "Saturn:Taurus":      { bullish: ["Banking", "FMCG", "Agri"],                     bearish: ["Luxury retail"] },
  "Saturn:Gemini":      { bullish: ["Telecom", "Logistics"],                        bearish: ["Media print"] },
  "Saturn:Cancer":      { bullish: ["Real estate", "Dairy"],                        bearish: ["Maritime", "Hotels"] },
  "Saturn:Leo":         { bullish: ["Gold mining", "Power", "Govt PSU"],            bearish: ["Entertainment"] },
  "Saturn:Virgo":       { bullish: ["Pharma APIs", "Auditing"],                     bearish: ["Quick-retail"] },
  "Saturn:Libra":       { bullish: ["Legal", "Diamonds", "Cosmetics"],              bearish: ["Liquor", "Speculation"] },
  "Saturn:Scorpio":     { bullish: ["Insurance", "Cyber-sec"],                      bearish: ["Crypto"] },
  "Saturn:Sagittarius": { bullish: ["Education", "Infra"],                          bearish: ["Airlines"] },
  "Saturn:Capricorn":   { bullish: ["Oil & gas", "Coal", "Cement"],                 bearish: ["Discretionary"] },
  "Saturn:Aquarius":    { bullish: ["Renewables", "Data infra"],                    bearish: ["Old banks"] },
  "Saturn:Pisces":      { bullish: ["Pharma R&D", "Shipping", "Hospitals"],         bearish: ["Alcohol"] },
  "Rahu:Aries":         { bullish: ["Defence-tech", "Crypto", "EV"],                bearish: ["Conservative banks"] },
  "Rahu:Taurus":        { bullish: ["Luxury banking", "Cosmetics"],                 bearish: ["Value funds"] },
  "Rahu:Gemini":        { bullish: ["Tech IPOs", "Social", "Adtech"],               bearish: ["Stable utilities"] },
  "Rahu:Cancer":        { bullish: ["RE speculation", "Food-delivery"],             bearish: ["Industrial commodities"] },
  "Rahu:Leo":           { bullish: ["Celebrity brands", "Crypto"],                  bearish: ["PSU"] },
  "Rahu:Virgo":         { bullish: ["AI/automation", "Detail-tech"],                bearish: ["Aviation"] },
  "Rahu:Libra":         { bullish: ["Dating apps", "Luxury exp"],                   bearish: ["Defence"] },
  "Rahu:Scorpio":       { bullish: ["Biotech", "Crypto", "Cyber-warfare"],          bearish: ["Plain retail"] },
  "Rahu:Sagittarius":   { bullish: ["Foreign ETFs", "Travel"],                      bearish: ["Local banks"] },
  "Rahu:Capricorn":     { bullish: ["Infra speculation", "Coal/oil plays"],         bearish: ["Glamour"] },
  "Rahu:Aquarius":      { bullish: ["AI", "Renewables", "Quantum"],                 bearish: ["Old infra"] },
  "Rahu:Pisces":        { bullish: ["AI-content", "Pharma mania", "Crypto"],        bearish: ["Traditional banking"] },
};


function ReportView({ report, narrative, narrativeBusy }) {
  const a = report.anchor_transits;
  return (
    <div className="mt-8 space-y-7" data-testid="report-view">
      {/* Anchor transits */}
      <section className="rounded-2xl p-6 bg-gradient-to-br from-slate-900 via-slate-900 to-amber-950/40 border border-amber-500/20" data-testid="anchor-transits">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300">Anchor transits · mid-year {report.year}</div>
            <h2 className="font-serif-display text-3xl mt-2 font-light">
              {report.is_historical ? `${report.year} · Historical` : `${report.year} · Forecast`}
            </h2>
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-4">
            <span className="text-emerald-400">●</span>{report.summary_stats.bull_months} bull mo
            <span className="text-rose-400">●</span>{report.summary_stats.bear_months} bear mo
            <span className="text-slate-400">●</span>{report.summary_stats.neutral_months} neutral mo
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          {["Jupiter", "Saturn", "Rahu"].map((p) => (
            <div key={p} className="rounded-xl p-4 bg-slate-900/60 border border-slate-700" data-testid={`anchor-${p}`}>
              <div className="text-[10px] uppercase tracking-widest text-amber-300">{p}</div>
              <div className="font-serif-display text-2xl mt-2 font-light">{a[p].rashi}</div>
              <div className="text-xs text-slate-400 mt-1">
                {a[p].nakshatra} · lord {a[p].nakshatra_lord}{a[p].retrograde && <span className="ml-1 text-rose-300">(R)</span>}
              </div>
              <div className="text-[10px] text-slate-500 mt-1 font-mono">{a[p].longitude}°</div>
            </div>
          ))}
        </div>
      </section>

      {/* Claude narrative */}
      <NarrativeSection narrative={narrative} narrativeBusy={narrativeBusy} />

      {/* Historical events in this year */}
      {(report.year_events.US.length > 0 || report.year_events.IN.length > 0) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="year-events">
          <EventColumn title={`US events in ${report.year}`} events={report.year_events.US} />
          <EventColumn title={`India events in ${report.year}`} events={report.year_events.IN} />
        </section>
      )}

      {/* Similar events */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="similar-events">
        <SimilarColumn title="Most-similar US analogues" events={report.similar_events.US} />
        <SimilarColumn title="Most-similar India analogues" events={report.similar_events.IN} />
      </section>

      {/* Monthly grid */}
      <section data-testid="monthly-grid">
        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2">
          <ChartLineUp size={14} weight="duotone" /> Month-by-month bias track
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {report.months.map((m) => (
            <MonthCard key={m.month} m={m} />
          ))}
        </div>
      </section>
    </div>
  );
}


function NarrativeSection({ narrative, narrativeBusy }) {
  if (narrativeBusy) {
    return (
      <section className="rounded-2xl p-6 bg-slate-900/60 border border-amber-500/30 flex items-center gap-3" data-testid="narrative-loading">
        <Sparkle size={18} weight="fill" className="text-amber-300 animate-pulse" />
        <span className="text-slate-300 font-light">Claude Sonnet 4.5 synthesising the verdict… (5-10s)</span>
      </section>
    );
  }
  if (!narrative) return null;
  return (
    <section className="rounded-2xl p-6 bg-gradient-to-br from-amber-950/40 via-slate-900 to-violet-950/40 border border-amber-400/40 space-y-5" data-testid="narrative">
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.25em]">
        <Sparkle size={14} weight="fill" /> Claude Sonnet 4.5 verdict
      </div>
      <h3 className="font-serif-display text-2xl sm:text-3xl font-light leading-snug">{narrative.headline}</h3>
      <p className="text-sm sm:text-base font-light leading-relaxed text-slate-200">{narrative.regime_paragraph}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl p-4 bg-slate-900/40 border border-slate-700" data-testid="us-read">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2">US read</div>
          <p className="text-sm text-slate-300 font-light leading-snug">{narrative.us_read}</p>
        </div>
        <div className="rounded-xl p-4 bg-slate-900/40 border border-slate-700" data-testid="india-read">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2">India read</div>
          <p className="text-sm text-slate-300 font-light leading-snug">{narrative.india_read}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.isArray(narrative.trade_calls) && narrative.trade_calls.length > 0 && (
          <div className="rounded-xl p-4 bg-emerald-950/40 border border-emerald-500/40" data-testid="trade-calls">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1.5"><TrendUp size={12} weight="bold" /> Trade</div>
            <ul className="space-y-2 text-sm text-emerald-100 font-light">
              {narrative.trade_calls.map((c, i) => (<li key={i} className="flex gap-2"><span className="text-emerald-400">→</span><span>{c}</span></li>))}
            </ul>
          </div>
        )}
        {Array.isArray(narrative.avoid_calls) && narrative.avoid_calls.length > 0 && (
          <div className="rounded-xl p-4 bg-rose-950/40 border border-rose-500/40" data-testid="avoid-calls">
            <div className="text-[10px] uppercase tracking-widest text-rose-300 mb-2 flex items-center gap-1.5"><TrendDown size={12} weight="bold" /> Avoid</div>
            <ul className="space-y-2 text-sm text-rose-100 font-light">
              {narrative.avoid_calls.map((c, i) => (<li key={i} className="flex gap-2"><span className="text-rose-400">⚠</span><span>{c}</span></li>))}
            </ul>
          </div>
        )}
      </div>
      {Array.isArray(narrative.key_inflection_months) && narrative.key_inflection_months.length > 0 && (
        <div className="rounded-xl p-4 bg-amber-950/40 border border-amber-500/40" data-testid="inflections">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-1.5"><Lightning size={12} weight="bold" /> Key inflection months</div>
          <div className="flex gap-2 flex-wrap">
            {narrative.key_inflection_months.map((m, i) => (
              <span key={i} className="px-2 py-1 rounded bg-amber-500/20 text-amber-200 text-xs font-mono">{m}</span>
            ))}
          </div>
        </div>
      )}
      {narrative.risk_warning && (
        <div className="rounded-xl p-3.5 bg-rose-950/30 border border-rose-500/30 flex items-start gap-2" data-testid="risk-warning">
          <Warning size={14} weight="duotone" className="text-rose-300 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-100 font-light leading-snug">{narrative.risk_warning}</p>
        </div>
      )}
    </section>
  );
}


function EventColumn({ title, events }) {
  if (!events.length) return null;
  return (
    <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700">
      <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-3">{title}</div>
      <div className="space-y-3">
        {events.map((e, i) => {
          const tint = KIND_TINT[e.kind] || KIND_TINT.crash;
          return (
            <div key={i} className={`rounded-xl p-3.5 ${tint.bg} ${tint.fg} ring-1 ${tint.ring}`} data-testid={`event-${e.region}-${i}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium text-sm">{e.label}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/60">{tint.label}</span>
              </div>
              <div className="text-xs mt-1 opacity-80 flex items-center gap-3">
                <span className="font-mono">{e.date}</span>
                <span className="font-bold">{e.drawdown > 0 ? "+" : ""}{e.drawdown}%</span>
                {e.recovery_months && <span className="opacity-70">recovery {e.recovery_months}mo</span>}
              </div>
              <p className="text-xs mt-2 leading-snug opacity-90">{e.summary}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function SimilarColumn({ title, events }) {
  return (
    <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700" data-testid={`similar-col-${title.includes('US') ? 'US' : 'IN'}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 mb-3">{title}</div>
      <div className="space-y-2.5">
        {events.length === 0 ? (
          <p className="text-xs text-slate-500 italic">No analogues found at the outer-planet level.</p>
        ) : (
          events.map((e, i) => {
            const tint = KIND_TINT[e.kind] || KIND_TINT.crash;
            return (
              <div key={i} className={`rounded-xl p-3 ${tint.bg} ${tint.fg} ring-1 ${tint.ring}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-medium text-sm">{e.label}</span>
                  <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-white/60">
                    {e.score}/{e.max_score}
                  </span>
                </div>
                <div className="text-xs mt-1 opacity-80 flex items-center gap-3">
                  <span className="font-mono">{e.date}</span>
                  <span className="font-bold">{e.drawdown > 0 ? "+" : ""}{e.drawdown}%</span>
                </div>
                <div className="text-[10px] mt-1.5 flex gap-2 opacity-90">
                  {["Saturn", "Jupiter", "Rahu"].map((p) => (
                    <span key={p} className={`px-1.5 py-0.5 rounded ${e.match[p] ? "bg-emerald-500/30 text-emerald-900" : "bg-slate-500/20 text-slate-700"}`}>
                      {p[0]}{e.match[p] ? "✓" : "·"}
                    </span>
                  ))}
                </div>
                <p className="text-xs mt-2 leading-snug opacity-90">{e.summary}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


function MonthCard({ m }) {
  const tint = BIAS_TINT[m.bias.band] || BIAS_TINT.neutral;
  return (
    <div className="rounded-xl p-4" style={{ background: tint.bg, color: tint.fg }} data-testid={`month-${m.month}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-sm font-bold">{m.month_label}</div>
        <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${tint.chip}`}>{m.bias.band.toUpperCase()}</span>
      </div>
      <div className="text-[10px] mt-2 opacity-80 grid grid-cols-3 gap-1">
        <div><span className="opacity-60">J</span> {m.transits.Jupiter.rashi.slice(0, 3)}</div>
        <div><span className="opacity-60">S</span> {m.transits.Saturn.rashi.slice(0, 3)}</div>
        <div><span className="opacity-60">R</span> {m.transits.Rahu.rashi.slice(0, 3)}</div>
      </div>
      {m.sector_picks.bullish.length > 0 && (
        <div className="mt-3">
          <div className="text-[9px] uppercase tracking-widest opacity-70 mb-1 flex items-center gap-1"><Briefcase size={9} /> Trade</div>
          <div className="text-[11px] opacity-90 line-clamp-3 leading-snug">{m.sector_picks.bullish.join(" · ")}</div>
        </div>
      )}
      {m.sector_picks.bearish.length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] uppercase tracking-widest opacity-70 mb-1 flex items-center gap-1"><Warning size={9} /> Avoid</div>
          <div className="text-[11px] opacity-80 line-clamp-2 leading-snug">{m.sector_picks.bearish.join(" · ")}</div>
        </div>
      )}
    </div>
  );
}
